import { scrubSecrets, type ReportKind, type ReportPriority } from './report';

/**
 * Tickets (owner's call 2026-10-22, report 181050-idea-2282863-6a9a: "change the bug report/suggestion feature over to a
 * ticket system. I should be able to assign priority and keep tickets open, close them, or reply to them … After each
 * update move all supposedly fixed tickets to a testing section where I can go to see what needs testing, test it, and
 * then close it"). Every bug report or suggestion sent from Torn's pages is a ticket:
 *
 *   waiting   a player's, not looked at yet — nobody works on it until the owner opens it (his say, 2026-10-19)
 *   open      to be worked on (the owner's own start here)
 *   progress  being worked on
 *   testing   fixed in a release, waiting for the owner to try it ("Testing")
 *   closed    done (tested, or nothing to do)
 *   declined  the owner said no
 *
 * The owner sets the priority (high / normal / low; the reporter's own pick to start with) and replies; the reporter
 * sees their own tickets and replies too; the developer's notes come with releases ("Fixed in 0.105.0 — please test").
 */

export const TICKET_STATUSES = ['waiting', 'open', 'progress', 'testing', 'closed', 'declined'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

/** What the owner sees for each status. */
export const TICKET_STATUS_NAMES: Record<TicketStatus, string> = {
  waiting: 'New — waiting for your OK',
  open: 'Open',
  progress: 'In progress',
  testing: 'Fixed — needs testing',
  closed: 'Closed',
  declined: 'Declined',
};

/** What the reporter sees for each status (plain words; nothing about other players). */
export const TICKET_STATUS_FOR_REPORTER: Record<TicketStatus, string> = {
  waiting: 'Received — not looked at yet',
  open: 'Open — on the list',
  progress: 'Being worked on',
  testing: 'Fixed — being tested',
  closed: 'Closed',
  declined: 'Not planned',
};

/** Who wrote a reply: the owner, the person who sent the ticket, or the developer (notes that come with a release). */
export type TicketRole = 'owner' | 'reporter' | 'dev';

export interface TicketReply {
  at: number;
  by: { id: number; name: string };
  role: TicketRole;
  text: string;
  /** A Torn page where the fix can be tried ("Test it here"; https://www.torn.com/… only). */
  url?: string;
}

/**
 * A ticket's history, like a GitHub issue's (owner's call 2026-10-23, 152952-idea-2282863-37e8: "If I reopen/close one the
 * prior info shouldn't be lost. It should function a bit more like a github repo issues page"): the report itself, every
 * reply, every change of status or priority and every one of the owner's notes, oldest first — nothing is ever
 * overwritten. Notes are private: the reporter's view never has them (`publicTimeline`).
 *
 *   opened    the report was sent (its text is in the ticket itself)
 *   reply     a reply (owner, reporter or the developer's note with a release; may carry a "Test it here" link)
 *   status    from → to (who moved it: the owner, or a release → Testing)
 *   priority  from → to
 *   note      the owner's private note for whoever fixes it (text '' = he cleared it)
 *   pictures  pictures added to the ticket later (v0.111.0, 183258-idea-2282863-d8dc: "upload new images in here to help
 *             diagnose problems after the 'fix'") — `files` names them (image-4.png …), `text` says what they show
 *   linked    a follow-up: a new ticket opened from this one, or this one opened from another (`ticket`: `<day>/<id>`)
 */
export const TICKET_EVENT_KINDS = ['opened', 'reply', 'status', 'priority', 'note', 'pictures', 'linked'] as const;
export type TicketEventKind = (typeof TICKET_EVENT_KINDS)[number];

export interface TicketEvent {
  at: number;
  kind: TicketEventKind;
  by: { id: number; name: string };
  role: TicketRole;
  text?: string;
  from?: string;
  to?: string;
  /** The release a fix came with. */
  version?: string;
  /** A Torn page where the fix can be tried. */
  url?: string;
  /** Only the owner sees it (his notes). */
  private?: boolean;
  /** Rebuilt from what was kept before timelines (v0.109.0): the time is the best one known. */
  migrated?: boolean;
  /** `pictures`: the files added (image-4.png …). */
  files?: string[];
  /** `linked`: the other ticket (`<day>/<id>`). */
  ticket?: string;
  /** `linked`: this one was opened from `ticket` ('from') or `ticket` was opened from this one ('to'). */
  link?: 'from' | 'to';
}

/** At most this many events on one ticket (replies are also capped at TICKET_REPLIES_MAX). */
export const TICKET_EVENTS_MAX = 600;

/** What the reporter may see of a timeline: everything but the owner's private notes. */
export function publicTimeline(events: readonly TicketEvent[]): TicketEvent[] {
  return events.filter((e) => !e.private && e.kind !== 'note');
}

/** The replies in a timeline (the thread as before timelines), oldest first. */
export function timelineReplies(events: readonly TicketEvent[]): TicketReply[] {
  return events.filter((e) => e.kind === 'reply' && typeof e.text === 'string').map((e) => ({ at: e.at, by: e.by, role: e.role, text: e.text!, ...(e.url ? { url: e.url } : {}) }));
}

/**
 * A link to a Torn page to test a fix on (suggestion 152307-idea-2282863-59ba: "add a link where we can to relevant pages
 * to be tested"): "/item.php" or "https://www.torn.com/item.php" → "https://www.torn.com/item.php"; anything else
 * (another site, javascript:, junk) → ''. Tokens are taken out of it.
 */
export function tornTestUrl(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const t = raw.trim();
  if (!t || t.length > 500) return '';
  let u: URL;
  try {
    u = new URL(t, 'https://www.torn.com/');
  } catch {
    return '';
  }
  if (u.protocol !== 'https:' || !/^(?:www\.)?torn\.com$/i.test(u.hostname) || u.username || u.password || u.port) return '';
  return scrubSecrets(`https://www.torn.com${u.pathname}${u.search}${u.hash}`);
}

/** The words for one timeline event ("changed the status: Open → Fixed — needs testing"), for the README and the lists. */
export function timelineLine(e: TicketEvent, names: Record<string, string> = TICKET_STATUS_NAMES): string {
  switch (e.kind) {
    case 'opened':
      return 'sent the ticket';
    case 'reply':
      return e.text ?? '';
    case 'status':
      return `changed the status: ${names[e.from ?? ''] ?? e.from ?? '?'} → ${names[e.to ?? ''] ?? e.to ?? '?'}${e.version ? ` (fixed in ${e.version})` : ''}`;
    case 'priority':
      return `changed the priority: ${e.from ?? '?'} → ${e.to ?? '?'}`;
    case 'note':
      return e.text ? `private note: ${e.text}` : 'cleared the private note';
    case 'pictures': {
      const n = e.files?.length ?? 0;
      return `added ${n} picture${n === 1 ? '' : 's'}${n ? ` (${e.files!.join(', ')})` : ''}${e.text ? `: ${e.text}` : ''}`;
    }
    case 'linked':
      return `${e.link === 'from' ? 'opened from the follow-up on' : 'follow-up opened as a new ticket:'} ${e.ticket ?? '?'}${e.text ? ` — ${e.text}` : ''}`;
  }
}

/** Pictures one ticket may hold in all (the report's own, plus those added later), and how many go in one go. */
export const TICKET_PICTURES_MAX = 20;
export const TICKET_PICTURES_AT_ONCE = 3;
/** A picture's file name in a ticket's folder (image-1.png … image-20.webp). */
export const TICKET_PICTURE = /^image-(\d{1,2})\.(png|jpg|webp|gif)$/;

/**
 * What the owner (or the reporter) wrote after the developer last spoke: his notes, their replies and pictures added
 * since the last developer entry (a reply, a status change by a release, a reopen or a linked follow-up). Owner, 2026-10-24:
 * "be sure to reopen issues that have follow up problems in the notes I give you" — on a Closed or Testing ticket these are
 * follow-ups nobody answered yet (QUEUE.md lists them first). Any developer entry answers them.
 */
export function unansweredFollowUps(events: readonly TicketEvent[]): TicketEvent[] {
  let last = -1;
  events.forEach((e, i) => {
    if (e.role === 'dev') last = i;
  });
  return events.slice(last + 1).filter((e) => (e.kind === 'note' && e.role === 'owner' && !!e.text) || (e.kind === 'reply' && e.role !== 'dev') || (e.kind === 'pictures' && e.role !== 'dev'));
}

/**
 * The developer's answer to a follow-up (docs/ticket-followups.json → apps/api/src/release-fixes.ts, applied once when the
 * server starts on that release): `reopen` the ticket (back to Open, with the developer's note), open a `new` ticket for it
 * (the owner's, linked both ways — when the follow-up is a different problem), or just `reply` (answered, nothing to do).
 */
export interface ReleaseFollowup {
  /** `<day>/<id>` of the ticket the follow-up is on (or the bare id). */
  from: string;
  /** `project`: the owner said to park it — it becomes one of his Projects (title + notes) and the ticket closes. */
  action: 'reopen' | 'new' | 'reply' | 'project';
  /** `new`: the new ticket's text's first line (else the follow-up's own words); `project`: the project's title. */
  title?: string;
  /** The developer's note (reopen / reply), the new ticket's text (new), or the project's notes (project). */
  text?: string;
}

export const TICKET_REPLY_MAX = 2_000;
/** At most this many replies on one ticket. */
export const TICKET_REPLIES_MAX = 200;
export const TICKET_ID = /^\d{6}-(?:bug|idea)-\d+-[0-9a-f]{4}$/;

/** A reply's text as it's kept: trimmed, within the limit, nothing that looks like a key. Empty when there's nothing. */
export function cleanReplyText(raw: unknown): string {
  return typeof raw === 'string' ? scrubSecrets(raw.trim().slice(0, TICKET_REPLY_MAX)) : '';
}

/** A ticket as its reporter sees it (their own only) — and the base of the owner's view. */
export interface TicketView {
  id: string;
  day: string;
  status: TicketStatus;
  kind: ReportKind;
  priority: ReportPriority;
  at: number;
  /** Last change (status, priority or a reply). */
  updatedAt: number;
  title: string;
  url: string;
  text: string;
  expected: string;
  /** The release it was fixed in (testing / closed after a fix). */
  fixedIn?: string;
  replies: TicketReply[];
  /** Everything that happened, oldest first (the reporter's: without the owner's private notes). */
  timeline?: TicketEvent[];
  /** Where to try the fix: the newest "Test it here" link, or (while it's in Testing) the ticket's own page. */
  testUrl?: string;
  /** The reporter hid it from "My reports" (done tickets only; a new reply brings it back). */
  hidden?: boolean;
}

/** The owner's view of a ticket. */
export interface TicketSummary extends TicketView {
  by: { id: number; name: string };
  /** Sent by the owner himself. */
  fromOwner: boolean;
  /** His private note for whoever fixes it (never shown to the reporter). */
  note?: string;
  /** Where it came from: "[PDA 0.103.0 · Android phone]" (reportTag). */
  tag: string;
  /** Pictures the owner can look at (screenshot.jpg, image-1.png …). */
  pictures: string[];
  /** The reporter wrote last and nobody has answered yet. */
  replyWaiting: boolean;
  /** Closed or in Testing, with the owner's note or a reply after the developer last spoke — a follow-up to answer. */
  followUp?: boolean;
}

/** One line of the release's list of fixed tickets (scripts/tickets-fixed.mjs → apps/api/src/release-fixes.ts). */
export interface ReleaseFix {
  /** `<day>/<id>` as in Debug\Reports (or the bare id). */
  id: string;
  /** The release that fixed it; empty when it was handled without a fix (skipped, "later"). */
  version: string;
  /** The developer's note for the ticket's thread (optional; the reporter sees it). */
  reply?: string;
  /** A Torn page to try the fix on (optional; the ticket's own page when there's none). */
  url?: string;
}

/**
 * The first release with tickets. Fixes from older releases were already tested the old way: their tickets are closed
 * straight away instead of waiting in Testing.
 */
export const TICKETS_SINCE = '0.105.0';

/** The leading version in a docs/reports-done.json value ("0.101.0 (weights on each seat…)" → "0.101.0"), or ''. */
export function fixVersion(done: string): string {
  return /^\s*v?(\d{1,4}\.\d{1,4}\.\d{1,4})\b/.exec(done)?.[1] ?? '';
}
